import axios from 'axios';
import { Subject } from 'rxjs';
import { Gizmo } from '../../gizmos';
import { ISingleFeature, TButtonOperation } from '../../types';
import { timeout } from '../../utils';
import CommonBinder from '../common-binder';

interface IButtonBinder {
  init(): Promise<void>;
}

interface IButtonData {
  file?: File;
  filename?: string;
}

class ButtonBinder extends CommonBinder implements IButtonBinder {
  public data: Subject<IButtonData>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.data = new Subject<IButtonData>();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (config.ops?.button?.download?.url) {
        const url = config.ops.button.download.url;

        if (url.includes('{{')) {
          // This means it has influx variables so we have to solve them
          const flux = this._gizmo.getFlux();
          const result = await flux?.interpolate({ script: url });

          if (result !== undefined) {
            const singleFeatures: ISingleFeature[] = [
              {
                path: 'button.url',
                value: result,
              },
            ];
            if (config.ops?.button?.download?.filename) {
              singleFeatures.push({
                path: 'button.filename',
                value: config.ops.button.download.filename,
              });
            }

            this._gizmo.setFeatures({
              singleFeatures,
            });
          }
        }
      }
      resolve();
    });
  };

  /**
   * Dispatch an operation to the core
   *
   * @param {TButtonOperation} operation button operation that will be dispatched
   * @returns
   */
  public dispatchOperation = ({
    operation,
  }: ISendOperationParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      // console.log('ButtonBinder.dispatchOperation', operation);
      // We set the processing feature to true, then it will be reseted after form submit
      // is finished
      this._gizmo.setFeatures({
        singleFeatures: [{ path: 'processing', value: true }],
      });

      if (operation !== 'download') {
        await this._gizmo.dispatchGizmoOperation({ operation });

        if (
          operation === 'section.next' ||
          operation === 'section.back' ||
          operation === 'navigation.link'
        ) {
          await timeout(100);
          this._gizmo.setFeatures({
            singleFeatures: [{ path: 'processing', value: false }],
          });
        }
      } else {
        const features = this._gizmo.getFeatures();

        if (features.button?.url) {
          const file = await this._downloadUrl({
            url: features.button.url,
            filename: features.button.filename,
          });

          if (file) {
            this.data.next({
              file,
              filename: features.button.filename,
            });
          }
        }

        await timeout(100);

        this._gizmo.setFeatures({
          singleFeatures: [{ path: 'processing', value: false }],
        });
      }

      resolve();
    });
  };

  private _downloadUrl = async ({
    url,
    filename,
  }: IDownloadUrlParams): Promise<File | undefined> => {
    return new Promise<File | undefined>(async resolve => {
      try {
        const response = await axios({
          method: 'GET',
          url,
          responseType: 'blob',
          // TODO: credentials...
        });

        if (response.status === 200) {
          let type = '';
          if (filename?.includes('pdf')) {
            type = 'application/pdf';
          } else if (filename?.includes('jpg')) {
            type = 'image/jpeg';
          }

          const file = new File(
            [response.data],
            filename || 'downloaded_file',
            { type }
          );

          return resolve(file);
        }
      } catch (e) {
        // Send notification error
        this._gizmo
          .getFoundation()
          ?.getGlobal()
          ?.sendNotification({
            notification: {
              fid: 'n0',
              type: 'error',
              label: 'Error al descargar el archivo',
            },
          });
      }

      resolve(undefined);
    });
  };
}

interface ISendOperationParams {
  operation: TButtonOperation;
}

interface IDownloadUrlParams {
  url: string;
  filename?: string;
}

export default ButtonBinder;
